// bom.ts — resolve a configuration into its BILL OF MATERIALS: the real Shopify
// pieces ({ variantId, quantity }) that make up the current build.
//
// This is the spine of Shopify-sourced mode. The price + stock of those variants
// come from the catalog proxy; this file only decides WHICH variants and how many,
// from data the config already carries (a forme's module instances, grouped by the
// sellable piece each module maps to). Pure: no network, no DOM, fully unit-tested.

import type { ProductConfig, ConfigState } from "@/types";

export interface BomLine {
  /** The sellable piece this line is (or `leg:<legId>` for a leg line). */
  pieceId: string;
  /** The Shopify variant id (numeric, as a string). */
  variantId: string;
  /** How many of this variant the configuration needs. */
  quantity: number;
}

export type BomResult =
  | { ok: true; lines: BomLine[] }
  | { ok: false; reason: "not-shopify" | "no-forme"; missing?: undefined }
  | { ok: false; reason: "unmapped"; missing: string[] };

/** The fabric ids that can select a piece variant, most-specific first. */
export function fabricKeys(state: ConfigState): string[] {
  return [state.colorwayId, state.materialId, state.fabricId].filter(
    (x): x is string => typeof x === "string" && x.length > 0,
  );
}

/** Resolve one piece's variant for the current fabric selection (override → default).
 *  Accepts any binding shape that carries byFabric + variantId (PieceBinding or FormeBinding). */
export function variantForPiece(pb: { byFabric?: Record<string, string>; variantId?: string }, state: ConfigState): string | undefined {
  for (const key of fabricKeys(state)) {
    const v = pb.byFabric?.[key];
    if (v) return v;
  }
  return pb.variantId || undefined;
}

/**
 * Resolve the current configuration to its Shopify line items.
 *
 *  - `{ ok: false, reason: "not-shopify" }` — no active binding; use the
 *    made-to-order path (the current default). Not an error.
 *  - `{ ok: false, reason: "no-forme" }` — the state's formeId is unknown.
 *  - `{ ok: false, reason: "unmapped", missing: [...] }` — one or more pieces are
 *    not mapped to a variant for this fabric. The caller greys "Add to cart" and
 *    can surface which pieces are missing (onboarding safety net, never a crash).
 *  - `{ ok: true, lines }` — the real cart payload.
 */
export function resolveBOM(product: ProductConfig, state: ConfigState): BomResult {
  const binding = product.shopify;
  if (!binding || binding.mode !== "shopify-sourced") {
    return { ok: false, reason: "not-shopify" };
  }

  const forme = product.formes.find((f) => f.id === state.formeId);
  if (!forme) return { ok: false, reason: "no-forme" };

  // Config-level binding: the whole shape maps to ONE sellable variant (quantity 1),
  // priced + stocked independently per shape × fabric. Takes precedence over module-summing.
  const fb = binding.byForme?.[forme.id];
  if (fb) {
    const variantId = variantForPiece(fb, state);
    if (!variantId) return { ok: false, reason: "unmapped", missing: [forme.id] };
    const lines: BomLine[] = [{ pieceId: forme.id, variantId, quantity: 1 }];
    const legV = binding.legs?.[state.legId];
    if (legV) lines.push({ pieceId: `leg:${state.legId}`, variantId: legV, quantity: 1 });
    return { ok: true, lines };
  }

  // Count pieces by pieceId (a module's pieceId, else its own id). Repeated seats
  // (section-regular / -b / -c) collapse to quantity N of one sellable variant.
  const counts = new Map<string, number>();
  for (const inst of forme.modules) {
    const mod = product.modules.find((m) => m.id === inst.moduleId);
    const pieceId = mod?.pieceId ?? inst.moduleId;
    counts.set(pieceId, (counts.get(pieceId) ?? 0) + 1);
  }

  const lines: BomLine[] = [];
  const missing: string[] = [];
  for (const [pieceId, quantity] of counts) {
    const pb = binding.pieces?.find((p) => p.pieceId === pieceId);
    const variantId = pb ? variantForPiece(pb, state) : undefined;
    if (!variantId) {
      missing.push(pieceId);
      continue;
    }
    lines.push({ pieceId, variantId, quantity });
  }

  // Legs as their own line item, when the binding maps the chosen leg.
  const legVariant = binding.legs?.[state.legId];
  if (legVariant) {
    lines.push({ pieceId: `leg:${state.legId}`, variantId: legVariant, quantity: 1 });
  }

  if (missing.length) return { ok: false, reason: "unmapped", missing };
  return { ok: true, lines };
}

/**
 * A stable, order-independent signature of the resolved variants, so the caller
 * can cache catalog (price + stock) lookups and skip redundant proxy calls while
 * the shopper tweaks non-commerce things (camera, a fabric that maps to the same
 * variant). Empty string when there is nothing to price.
 */
export function bomSignature(result: BomResult): string {
  if (!result.ok) return "";
  return result.lines
    .map((l) => `${l.variantId}:${l.quantity}`)
    .sort()
    .join("|");
}
